/**
 * web/src/ui/Confetti.tsx — the one confetti layer (card psHEfEkK9cT2).
 *
 * Mounted once in App. Surfaces never paint particles themselves; they call
 * `celebrateOnce(key)` and this layer decides whether a burst is earned:
 *   - off  → the Settings preference said no, nothing renders;
 *   - reduced motion → the caller's own toast/line is the moment, no particles;
 *   - on   → 18 particles for 1.7s, then the layer unmounts.
 *
 * Portalled to document.body like the kudos toast: the shell's panels carry a
 * transform (the fade-in), and a transformed ancestor would anchor
 * `position: fixed` to the panel instead of the viewport.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CELEBRATE_EVENT, confettiEnabled } from '../lib/celebrate';
import '../styles/confetti.css';

const PARTICLES = 18;
const BURST_MS = 1700;

export function ConfettiLayer() {
  /* A counter, not a boolean: a second burst inside the first restarts the
     layer (the key remounts the particles) instead of being swallowed. */
  const [burst, setBurst] = useState<number | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const onCelebrate = () => {
      if (!confettiEnabled()) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      setBurst((n) => (n ?? 0) + 1);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setBurst(null);
        timerRef.current = null;
      }, BURST_MS);
    };
    window.addEventListener(CELEBRATE_EVENT, onCelebrate);
    return () => {
      window.removeEventListener(CELEBRATE_EVENT, onCelebrate);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  if (burst === null) return null;

  return createPortal(
    <div className="pear-confetti" data-confetti-burst={burst} aria-hidden="true">
      {Array.from({ length: PARTICLES }).map((_, i) => (
        <span
          key={i}
          className="pear-confetti-particle"
          style={{
            left: `${4 + (i % 9) * 11}%`,
            animationDelay: `${(i % 6) * 0.07}s`,
            ['--confetti-hue' as string]: `${(i * 47) % 360}`,
          }}
        />
      ))}
    </div>,
    document.body,
  );
}
